/**
 * `useWebSocket` — Zustand store + connection orchestrator (T-008).
 *
 * Owns:
 *   - The single `MirlaiWebSocket` instance for the app
 *   - Status mirror (`connecting` / `open` / `closed` / `reconnecting`)
 *   - "Disconnected since" timer (drives the > 5 s "Reconnecting…"
 *     banner in T-030)
 *   - Auth-store subscription: reconnects when the active JWT rotates,
 *     closes when it goes null
 *   - Visibility coordination: pauses reconnect when the tab hides,
 *     resumes immediately on un-hide
 *
 * Exposes:
 *   - The Zustand hook itself for React reads
 *   - `initWebSocket()` — the one-shot wiring entry called from
 *     `main.tsx` after `initAuth()`. Idempotent + returns a teardown
 *     function (tests use it; production never tears down).
 *
 * Per F-310 technical spec § 9 and task T-008.
 */
import { create } from "zustand";

import { useAuth } from "./auth-store";
import { buildWebSocketUrl, MirlaiWebSocket } from "./websocket";

/**
 * High-level connection state, distinct from the raw WS `readyState`.
 *
 *  - `closed`        — no socket, no pending reconnect (boot, post-logout)
 *  - `connecting`    — socket opening for the first time this attempt
 *  - `open`          — `auth_ok` received; subscribe sent; fully usable
 *  - `reconnecting`  — last attempt failed; backoff timer is running
 */
export type WSStatus = "closed" | "connecting" | "open" | "reconnecting";

export interface WSStoreState {
  status: WSStatus;
  /**
   * Milliseconds since the connection last left `open`. `null` while
   * connected. Ticks every 1 s while disconnected so consumers can do
   * `disconnectedSinceMs > 5000 && show banner` without their own
   * timer plumbing.
   */
  disconnectedSinceMs: number | null;
  /** Human-readable last error. `null` after a successful (re)connect. */
  lastError: string | null;
  /** Last known WS URL (for diagnostics). */
  url: string | null;

  /** Open the connection if not already connecting/open. Safe to call repeatedly. */
  connect(): void;
  /** Explicitly close — no reconnect until the next `connect()`. */
  disconnect(): void;
  /** Re-subscribe to a topic list (or refresh the current set). */
  subscribe(topics: ReadonlyArray<string>): void;
}

/**
 * Disconnected-since tick interval. 1 s is fine — the consumer only
 * cares about a 5 s threshold, so sub-second precision is wasted work.
 */
const DISCONNECT_TICK_MS = 1000;

/**
 * Module-singleton state. The wrapper instance and timer handles live
 * outside Zustand so they don't trigger re-renders on assign, and so
 * `initWebSocket()` can tear them down explicitly between tests.
 */
let wsInstance: MirlaiWebSocket | null = null;
let disconnectedSince: number | null = null;
let disconnectTicker: ReturnType<typeof setInterval> | null = null;
let authUnsubscribe: (() => void) | null = null;
let visibilityHandler: (() => void) | null = null;
let initialised = false;

/**
 * Resolve the gateway base URL the same way `gateway-client.ts` does
 * — read `VITE_GATEWAY_BASE_URL`, fall back to the documented dev
 * default. Wrapped in a try so non-Vite test environments (where
 * `import.meta.env` may be undefined) don't crash on module load.
 */
function resolveGatewayBaseUrl(): string | null {
  try {
    const explicit = import.meta.env?.VITE_GATEWAY_BASE_URL as string | undefined;
    if (explicit) return explicit;
    // Same-origin by default (issue #532): use the page origin so the kiosk
    // (served at 127.0.0.1:8200) opens its WebSocket to its OWN host. A WS URL
    // can't be relative, and an absolute `localhost` default would be a
    // cross-origin socket the appliance's WPEWebKit refuses. Falls through to
    // `buildWebSocketUrl`'s dev default off-browser (tests / SSR).
    if (typeof window !== "undefined" && window.location?.origin) {
      return window.location.origin;
    }
    return null;
  } catch {
    return null;
  }
}

export const useWebSocket = create<WSStoreState>((set, get) => ({
  status: "closed",
  disconnectedSinceMs: null,
  lastError: null,
  url: null,

  connect(): void {
    ensureWrapper();
    if (!wsInstance) return;
    // Reading status off the wrapper would be racy — Zustand's
    // post-condition is what UI observes, so guard on that.
    const current = get().status;
    if (current === "open" || current === "connecting") return;
    wsInstance.connect();
  },

  disconnect(): void {
    if (!wsInstance) {
      set({ status: "closed", disconnectedSinceMs: null });
      stopDisconnectTicker();
      return;
    }
    wsInstance.close();
    set({ status: "closed", disconnectedSinceMs: null, lastError: null });
    stopDisconnectTicker();
  },

  subscribe(topics): void {
    wsInstance?.subscribe(topics);
  },
}));

/**
 * Lazily build the `MirlaiWebSocket` wrapper. Called from `connect()`
 * and from `initWebSocket()` — same construction either way so the
 * resolved URL + handlers stay in sync.
 */
function ensureWrapper(): void {
  if (wsInstance) return;
  const baseUrl = resolveGatewayBaseUrl();
  const url = buildWebSocketUrl(baseUrl);
  useWebSocket.setState({ url });

  wsInstance = new MirlaiWebSocket(
    url,
    () => useAuth.getState().getActiveJwt(),
    {
      onConnecting: () => {
        // First-time connection vs reconnect: if we already have a
        // baseline `disconnectedSince`, this is a retry, so reflect
        // that. Otherwise it's the initial open.
        useWebSocket.setState((s) => ({
          status: s.disconnectedSinceMs === null ? "connecting" : "reconnecting",
          lastError: null,
        }));
      },
      onOpen: () => {
        disconnectedSince = null;
        stopDisconnectTicker();
        useWebSocket.setState({
          status: "open",
          disconnectedSinceMs: null,
          lastError: null,
        });
      },
      onClose: () => {
        useWebSocket.setState({ status: "closed" });
        startDisconnectTicker();
      },
      onError: (msg) => {
        useWebSocket.setState({ lastError: msg });
      },
      onReconnectScheduled: () => {
        useWebSocket.setState({ status: "reconnecting" });
        startDisconnectTicker();
      },
    },
  );
}

function startDisconnectTicker(): void {
  if (disconnectedSince === null) {
    disconnectedSince = Date.now();
    useWebSocket.setState({ disconnectedSinceMs: 0 });
  }
  if (disconnectTicker !== null) return;
  disconnectTicker = setInterval(() => {
    if (disconnectedSince === null) return;
    useWebSocket.setState({ disconnectedSinceMs: Date.now() - disconnectedSince });
  }, DISCONNECT_TICK_MS);
}

function stopDisconnectTicker(): void {
  disconnectedSince = null;
  if (disconnectTicker !== null) {
    clearInterval(disconnectTicker);
    disconnectTicker = null;
  }
}

/**
 * Wire the WebSocket layer to the auth store + document visibility.
 *
 * Call exactly once from `main.tsx`, after `initAuth()`. Idempotent:
 * a second call is a no-op until the previous teardown function runs.
 *
 * Returns a teardown function — tests use it to reset between specs;
 * production discards it (the WS layer lives the lifetime of the app).
 *
 * Behaviour:
 *   1. If `useAuth.getActiveJwt()` is non-null at call time, connects
 *      immediately. (Pre-login boot stays disconnected; the first
 *      successful login triggers the auth-subscribe path below.)
 *   2. Subscribes to `useAuth` state changes. When the active JWT
 *      rotates, closes + reopens so the new token is used in the auth
 *      frame. When the JWT goes null (logout), closes and stays down.
 *   3. Subscribes to `document.visibilitychange`. Hidden → `pause()`,
 *      visible → `resume()`. Pre-existing connections stay alive
 *      when the tab is hidden (per task § 7: don't kill, just stop
 *      retrying).
 */
export function initWebSocket(): () => void {
  if (initialised) {
    // Hand back a no-op teardown so callers can compose unconditionally.
    return () => {};
  }
  initialised = true;
  ensureWrapper();

  // ---- 1. Initial connect if a token is already present.
  const initialJwt = useAuth.getState().getActiveJwt();
  if (initialJwt) {
    useWebSocket.getState().connect();
  }

  // ---- 2. Auth-store subscription for token rotation.
  // Track the last observed token so we only react on actual change,
  // not on every unrelated `useAuth.setState` (mode flips, user/tier
  // hydration, etc.).
  let lastToken = initialJwt;
  authUnsubscribe = useAuth.subscribe((state) => {
    const next = state.getActiveJwt();
    if (next === lastToken) return;
    lastToken = next;
    if (!wsInstance) return;
    if (!next) {
      // Logout: close + stay down.
      useWebSocket.getState().disconnect();
      return;
    }
    // Token rotated: close + reopen so the auth frame carries the
    // new token. `close()` cancels the pending reconnect (if any);
    // `connect()` immediately re-arms.
    wsInstance.close();
    // `close()` sets `explicitClose`; `connect()` resets it.
    useWebSocket.setState({ status: "connecting", lastError: null });
    wsInstance.connect();
  });

  // ---- 3. Visibility coordination.
  if (typeof document !== "undefined" && typeof document.addEventListener === "function") {
    visibilityHandler = (): void => {
      if (!wsInstance) return;
      if (document.visibilityState === "hidden") {
        wsInstance.pause();
      } else if (document.visibilityState === "visible") {
        wsInstance.resume();
      }
    };
    document.addEventListener("visibilitychange", visibilityHandler);
  }

  return teardownWebSocket;
}

/**
 * Tear down the singleton — closes the socket, unsubscribes from the
 * auth store + visibility, resets the Zustand state to its boot
 * baseline. Tests call this in `afterEach`; production never calls it.
 *
 * Exported in addition to being the return value of `initWebSocket()`
 * so non-test callers (e.g. a hypothetical "log out everywhere") can
 * reset the layer without holding onto the init return value.
 */
export function teardownWebSocket(): void {
  if (!initialised) return;
  initialised = false;
  if (authUnsubscribe) {
    authUnsubscribe();
    authUnsubscribe = null;
  }
  if (visibilityHandler && typeof document !== "undefined") {
    document.removeEventListener("visibilitychange", visibilityHandler);
    visibilityHandler = null;
  }
  if (wsInstance) {
    wsInstance.close();
    wsInstance = null;
  }
  stopDisconnectTicker();
  useWebSocket.setState({
    status: "closed",
    disconnectedSinceMs: null,
    lastError: null,
    url: null,
  });
}

/**
 * Test-only inspection hooks. Production code MUST NOT depend on
 * these — `useWebSocket` is the supported public surface.
 */
export const __internal = {
  getWrapper: (): MirlaiWebSocket | null => wsInstance,
  isInitialised: (): boolean => initialised,
  // `forceTick` lets tests advance the disconnected-since counter
  // without waiting on real timers.
  forceTick: (): void => {
    if (disconnectedSince === null) return;
    useWebSocket.setState({ disconnectedSinceMs: Date.now() - disconnectedSince });
  },
};
